import { useContext, useState, useEffect } from 'react';
import { AppBar, CssBaseline, Divider, Drawer, Hidden, IconButton, List, ListItem, ListItemIcon, ListItemText, Toolbar, Typography, LinearProgress, Dialog } from '@material-ui/core';
import { AuthContext, NavContext, NavRoutes, UserContext, SettingsContext } from '../contexts';
import { CrumbsBar } from './CrumbsBar';
import { ApplicationRoles } from '../../helpers';
import MenuIcon from '@material-ui/icons/Menu';
import LockIcon from '@material-ui/icons/LockOutlined';
import ExitToAppIcon from '@material-ui/icons/ExitToAppOutlined';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { AuthenticationResult } from '@azure/msal-browser';
import { aquireToken } from '../contexts/AuthContext';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { _sessionStorageKey } from '../../helpers/Utils';

const drawerWidth = 240;

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
    },
    drawer: {
        [theme.breakpoints.up('lg')]: {
            width: drawerWidth,
            flexShrink: 0
        },
    },
    appBar: {
        backgroundColor: '#f5f2f0',
        [theme.breakpoints.up('lg')]: {
            width: `calc(100% - ${drawerWidth}px)`,
            marginLeft: drawerWidth
        },
    },
    menuButton: {
        marginRight: theme.spacing(2),
        [theme.breakpoints.up('lg')]: {
            display: 'none'
        }
    },
    toolbar: theme.mixins.toolbar,
    toolbarButton: {
        margin: theme.spacing(1)
    },
    drawerPaper: {
        width: drawerWidth,
        backgroundColor: '#f5f2f0'
    },
    content: {
        flexGrow: 1,
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(3),
        },
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1),
        },
    },
    title: {
        flexGrow: 1
    },
    user: {
        margin: theme.spacing(1)
    },
    dialog: {
        maxWidth: '75vw'
    }
}));

export const Layout = (props) => {
    const { logOut } = useContext(AuthContext);
    const { getAppSettingsS4, getAppSettingsECC } = useContext(SettingsContext);
    const { menuItems, navigateTo } = useContext(NavContext);
    const { container } = props;
    const classes = useStyles();
    const theme = useTheme();
    const { instance } = useMsal();
    const isAuthenticated: boolean = useIsAuthenticated();

    const {
        userInfo,
        getUserInfo,
        isUserInRoles
    } = useContext(UserContext);

    const [mobileOpen, setMobileOpen] = useState(false);
    const [username, setUsername] = useState(null);
    const [ready, setIsReady] = useState(false);
    const [isDialog, setIsDialog] = useState(false);

    const loginRequestedRoute = sessionStorage.getItem(_sessionStorageKey);
    const originalPrivacyPolicy: HTMLElement = document.getElementById("otnotice-26ab4484-ed83-439f-b8f9-948d2e7ccbc8");

    const privacyPolicyDialog = (): JSX.Element => {
        const clonedPrivacyPolicy = originalPrivacyPolicy.cloneNode(true) as HTMLElement;

        return (
            <Dialog
                open={isDialog}
                onClose={() => setIsDialog(false)}
                aria-labelledby="OneTrust Info Privacy"
                classes={{ paper: classes.dialog }}
            >
                <div style={{ padding: "30px" }} dangerouslySetInnerHTML={{ __html: clonedPrivacyPolicy.outerHTML }}></div>
            </Dialog>
        );
    };

    const drawerToggleHandler = () => {
        setMobileOpen(!mobileOpen);
    };

    const itemClickHandler = (item) => {
        navigateTo(item.routePath);
    }

    const logoutClickHandler = () => {
        logOut();
    }

    const goToAdminClickHandler = () => {
        navigateTo(NavRoutes.Admin.Dashboard);
    }

    const parseJwt = async (): Promise<void> => {
        const token: AuthenticationResult = await aquireToken(instance);
        const base64Url: string = token.accessToken.split('.')[1];
        const base64: string = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload: string = decodeURIComponent(
            atob(base64)
                .split('')
                .map((char: string): string =>
                    '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2)
                )
                .join('')
        );

        const parsedToken = JSON.parse(jsonPayload);
        setUsername(`${parsedToken.family_name} ${parsedToken.given_name}`);
        getAppSettingsS4();
        getAppSettingsECC();
        getUserInfo();
    };

    useEffect((): void => {
        if (isAuthenticated)
            parseJwt();
    }, [isAuthenticated]);

    useEffect(() => {
        if (username && userInfo?.externalKey !== null) {
            setIsReady(true);

            if (userInfo.roles === null || userInfo.roles.length === 0)
                navigateTo(NavRoutes.Forbidden);

            else if (!!loginRequestedRoute){
                navigateTo(loginRequestedRoute.replace(window.location.origin, ""));
                sessionStorage.removeItem(_sessionStorageKey);
            }
        }
    }, [userInfo]);

    const drawer = (
        <div>
            <div className={classes.toolbar} />
            <Divider />
            <List>
                {
                    menuItems &&
                    menuItems
                        .filter(item =>
                            (!item.isAdmin || item.isAdmin === false) &&
                            (item.roles === null || item.roles === undefined || isUserInRoles(item.roles))
                        )
                        .map((item, index) =>
                            <ListItem
                                key={index}
                                button
                                onClick={() => itemClickHandler(item)}
                            >
                                <ListItemIcon>{item.icon !== undefined ? item.icon : <MenuIcon />}</ListItemIcon>
                                <ListItemText primary={item.title || item.routePath} />
                            </ListItem>
                        )
                }
            </List>
        </div>
    );

    return (
        <div className={classes.root}>
            {
                ready ?
                <>
                    <CssBaseline />
                    <AppBar position="fixed" className={classes.appBar}>
                        <Toolbar>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={drawerToggleHandler}
                                className={classes.menuButton}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Typography variant="h6" noWrap className={classes.title}>
                                <CrumbsBar />
                            </Typography>
                            <Hidden smDown>
                                {
                                    originalPrivacyPolicy &&
                                    privacyPolicyDialog()
                                }

                                <Typography variant="body1" noWrap className={classes.user}>
                                    {
                                        originalPrivacyPolicy &&
                                        <Typography
                                            color="textSecondary"
                                            component="strong"
                                            onClick={() => setIsDialog(true)}
                                            style={{ cursor: 'pointer', marginRight: '50px' }}
                                        >
                                            Privacy Policy
                                        </Typography>
                                    }

                                    welcome <Typography color="textSecondary" component="strong">{username}</Typography>
                                </Typography>
                            </Hidden>
                                {isUserInRoles([ApplicationRoles.Administrator, ApplicationRoles.ProcessOwner, ApplicationRoles.DataQuality]) &&
                                    <IconButton aria-label="admin area" className={classes.toolbarButton} color="inherit" onClick={goToAdminClickHandler}>
                                        <LockIcon color="primary" />
                                    </IconButton>
                                }
                                <IconButton aria-label="logout" className={classes.toolbarButton} color="inherit" onClick={logoutClickHandler}>
                                    <ExitToAppIcon color="primary" />
                                </IconButton>
                            </Toolbar>
                        </AppBar>
                        <nav className={classes.drawer} aria-label="menu items">
                            {/* Mobile Screens */}
                            <Hidden lgUp implementation="js">
                                <Drawer
                                    container={container}
                                    variant="temporary"
                                    anchor={theme.direction === 'rtl' ? 'right' : 'left'}
                                    open={mobileOpen}
                                    onClose={drawerToggleHandler}
                                    classes={{
                                        paper: classes.drawerPaper,
                                    }}
                                    ModalProps={{
                                        keepMounted: true, // Better open performance on mobile.
                                    }}>
                                    {drawer}
                                </Drawer>
                            </Hidden>
                            {/* Desktop Screens */}
                            <Hidden mdDown implementation="js">
                                <Drawer
                                    classes={{
                                        paper: classes.drawerPaper,
                                    }}
                                    variant="permanent"
                                    open>
                                    {drawer}
                                </Drawer>
                            </Hidden>
                        </nav>
                        <main className={classes.content}>
                            <div className={classes.toolbar} />
                            {props.children}
                        </main>
                    </>
                    :
                    <>
                        <CssBaseline />
                        <main className={classes.content}>
                            <LinearProgress />
                        </main>
                    </>
            }
        </div>
    );
}